import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, DialogActions, DialogContent, InputAdornment } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TFormType, TPopupFormData } from 'src/@types';
import CEditor from 'src/components/editor/CEditor';
import FormProvider, { RHFSelect, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { RHFDateTimeRangePicker } from 'src/components/hook-form/RHFDatePicker';
import useGetSelectOption from 'src/hooks/useGetSelectOption';
import { FormItem, FormRow } from 'src/layouts/form/FormLayout';
import { IPopup, useStores } from 'src/models';
import { popupApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType } from 'src/static/constants/common/form';
import popupFormValidationSchema from 'src/static/yup/popupFormvalidationSchema';
import { getPopupFormDefaultValues } from 'src/utils/getRHFDefaultValues';

interface IPopupFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: IPopup;
}

export const PopupFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedData }: IPopupFormDialogProps) => {
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { alertStore, commonCodeStore } = useStores();
    const { getLang } = useGetSelectOption();
    const environmentOptions = commonCodeStore.getCCodeByName('POPUP_ENV_CD') ?? [];
    const langs = getLang();
    const isCreate = formType === FormType.CREATE;

    const methods = useForm<TPopupFormData>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      resolver: yupResolver(popupFormValidationSchema),
      defaultValues: getPopupFormDefaultValues(selectedData),
    });
    // 등록 / 수정 함수
    const onValid = async (data: TPopupFormData) => {
      const responseText = isCreate ? '등록' : '수정';
      const result = isCreate
        ? popupApi.post(data)
        : popupApi.put(selectedData?.popupSid ?? 0, data);
      if (await result) {
        handleResponse(
          `${responseText}이 완료되었습니다.`,
          `${responseText}이 실패되었습니다.`,
          onSubmitSuccess,
        );
      } else {
        console.error(`${responseText} 실패`);
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        popupApi
          .remove(selectedData?.popupSid ?? 0)
          .then(() =>
            handleResponse(
              '팝업 삭제가 완료되었습니다.',
              '팝업 삭제가 실패되었습니다.',
              onSubmitSuccess,
            ),
          );
      });
      alertStore.setProps({
        open: true,
        title: '팝업 삭제',
        content: '해당 팝업을 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };
    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)}>
        <DialogContent dividers sx={{ pt: '10px' }}>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFSwitch label={t('popup.form.useYn')} name="useYn" />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr 1fr' }}>
            <FormItem>
              <RHFSelect
                label={t('popup.form.langCd')}
                name="langCd"
                options={langs}
                useAll={false}
              />
            </FormItem>
            <FormItem>
              <RHFSelect
                label={t('popup.form.popupEnvCd')}
                name="popupEnvCd"
                options={environmentOptions}
                useAll={false}
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr 1fr' }}>
            <FormItem>
              <RHFTextField
                label={t('popup.form.inactiveTime')}
                name="inactiveTime"
                type="number"
              />
            </FormItem>
            <FormItem size={2}>
              <RHFDateTimeRangePicker
                label={t('popup.form.startDt')}
                name="startDt"
                startLabel="노출 시작일"
                endLabel="노출 종료일"
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr 1fr' }}>
            <FormItem>
              <RHFTextField
                label={t('popup.form.topPosition')}
                name="topPosition"
                type="number"
                allowZero={true}
              />
            </FormItem>
            <FormItem>
              <RHFTextField
                label={t('popup.form.leftPosition')}
                name="leftPosition"
                type="number"
                allowZero={true}
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr 1fr' }}>
            <FormItem>
              <RHFTextField
                label={t('popup.form.width')}
                name="width"
                InputProps={{
                  endAdornment: <InputAdornment position="end">px</InputAdornment>,
                }}
                type="number"
              />
            </FormItem>
            <FormItem>
              <RHFTextField
                label={t('popup.form.height')}
                name="height"
                InputProps={{
                  endAdornment: <InputAdornment position="end">px</InputAdornment>,
                }}
                type="number"
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField label={t('popup.form.title')} name="title" />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <CEditor name={'descript'} placeholder={t('popup.form.descript')} />
            </FormItem>
          </FormRow>
        </DialogContent>
        <DialogActions>
          <>
            {formType === FormType.UPDATE && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" color="primary">
              {formType === FormType.CREATE
                ? `${t('common.button.register')}`
                : `${t('common.button.edit')}`}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);
